/**
 * SOVRANOVA — ReflectionsHistory (Ambient Companion)
 * ------------------------------------------------------------
 * Your saved reflections — nothing vanishes after one read. Lists past
 * reflections (GET /api/ambient/reflections), opens one in full detail,
 * lets you delete it, and hands a reflection into the AI Session to talk
 * through again. Self-only.
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../../services/api'
import { useSettings } from '../../hooks/useSettings'
import { speakLong } from '../../services/speech'
import { ReflectionThread } from './ReflectionThread'

export function ReflectionsHistory({ onBack, onOpenSession, onNewReflection }) {
  // A saved reflection can be heard again, not just re-read. Same voice, same
  // full-length chunked path as when it was first given — coming back to a
  // reflection weeks later should feel like the same person saying it, rather
  // than opening a record of it.
  const { settings } = useSettings()
  const voiceOff = settings.textOnly || !settings.voiceEnabled
  const [speaking, setSpeaking] = useState(false)
  const ttsRef = useRef(null)
  const speakTokenRef = useRef(0)

  const hush = useCallback(() => {
    speakTokenRef.current++
    try { ttsRef.current?.pause() } catch { /* noop */ }
    setSpeaking(false)
  }, [])

  const speak = useCallback(async (text) => {
    if (!text || voiceOff) return
    const token = ++speakTokenRef.current
    setSpeaking(true)
    try { await speakLong(text, ttsRef, () => speakTokenRef.current !== token) }
    catch { /* the words are on screen regardless */ }
    if (speakTokenRef.current === token) setSpeaking(false)
  }, [voiceOff])

  useEffect(() => () => { speakTokenRef.current++; try { ttsRef.current?.pause() } catch {} }, [])
  useEffect(() => { if (voiceOff) hush() }, [voiceOff, hush])
  const [list, setList]     = useState(null)   // null = loading
  const [detail, setDetail] = useState(null)
  const [error, setError]   = useState(null)
  const [patterns, setPatterns] = useState(null)
  const [pLoading, setPLoading]   = useState(false)
  const [themeFilter, setThemeFilter] = useState(null)

  useEffect(() => { load() }, [])

  const load = async () => {
    setError(null)
    try {
      const res = await api.get('/ambient/reflections')
      setList(res.reflections || [])
    } catch {
      setError('Could not load your reflections just now.')
      setList([])
    }
  }

  const open = async (id) => {
    setError(null)
    try { setDetail(await api.get(`/ambient/reflections/${id}`)) }
    catch { setError('Could not open that reflection.') }
  }

  const remove = async (id) => {
    try { await api.post(`/ambient/reflections/${id}/delete`); setDetail(null); load() }
    catch { setError('Could not delete that reflection.') }
  }

  const loadPatterns = async () => {
    if (pLoading) return
    setPLoading(true)
    try { setPatterns(await api.get('/ambient/patterns')) }
    catch { setPatterns({ enough: false, patterns: [] }) }
    finally { setPLoading(false) }
  }

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) }
    catch { return '' }
  }

  // ── Detail view
  if (detail) {
    return (
      <div style={s.wrap}>
        <div style={s.top}><button style={s.back} onClick={() => { hush(); setDetail(null) }}>← Reflections</button></div>
        <p style={s.date}>{fmtDate(detail.generated_at)}</p>
        {detail.reflection && <p style={s.reflection}>{detail.reflection}</p>}
        {!voiceOff && detail.reflection && (
          <button style={s.voiceBtn} onClick={() => (speaking ? hush() : speak(detail.reflection))}>
            {speaking ? '■ Stop' : '🔊 Hear this reflection'}
          </button>
        )}
        {detail.summary && (
          <div style={s.block}><p style={s.label}>What I heard</p><p style={s.summary}>{detail.summary}</p></div>
        )}
        {Array.isArray(detail.themes) && detail.themes.length > 0 && (
          <div style={s.block}><p style={s.label}>Themes</p>
            <div style={s.chips}>{detail.themes.map((t, i) => <span key={i} style={s.chip}>{t}</span>)}</div>
          </div>
        )}
        {Array.isArray(detail.key_moments) && detail.key_moments.length > 0 && (
          <div style={s.block}><p style={s.label}>Moments that stood out</p>
            <ul style={s.moments}>{detail.key_moments.map((m, i) => <li key={i} style={s.moment}>{m}</li>)}</ul>
          </div>
        )}
        {detail.debrief && (
          <div style={s.block}><p style={s.label}>Your words</p><p style={s.debrief}>{detail.debrief}</p></div>
        )}

        {/* Pick the conversation back up where it stopped — weeks later if need
            be. Existing turns come back from the server with the reflection, so
            reopening this is continuing, not starting over. autoSpeak is off:
            reopening an old reflection shouldn't start talking at you. */}
        <ReflectionThread
          insightId={detail.id}
          initialTurns={Array.isArray(detail.conversation) ? detail.conversation : []}
          voiceOff={voiceOff}
          autoSpeak={false}
        />

        {error && <p style={s.error}>{error}</p>}
        <div style={s.controls}>
          {onOpenSession && <button style={s.primary} onClick={() => { hush(); onOpenSession({ summary: detail.summary, reflection: detail.reflection, debrief: detail.debrief }) }}>Continue this in a session</button>}
          <button style={s.danger} onClick={() => { hush(); remove(detail.id) }}>Delete</button>
        </div>
      </div>
    )
  }

  // ── List view — the FRONT DOOR of Reflect (19 Aug). People land here first,
  // pick the reflection they want to continue (its recordings and thread stay
  // archived with it), or explicitly start a new one. Landing straight on the
  // record screen made it too easy to record a debrief that belonged with an
  // existing reflection.
  return (
    <div style={s.wrap}>
      <div style={s.top}><button style={s.back} onClick={onBack}>← Journey</button></div>
      <div style={s.head}>
        <div style={s.icon}>📖</div>
        <h1 style={s.title}>Your reflections</h1>
        <p style={s.sub}>Open one to keep reflecting on it — everything you add stays with that reflection. Or start a new one.</p>
      </div>

      {onNewReflection && (
        <button style={s.newBtn} onClick={() => { hush(); onNewReflection() }}>
          🎙 Reflect on something new
        </button>
      )}

      {list && list.length >= 3 && !patterns && (
        <button style={s.patternsBtn} onClick={loadPatterns} disabled={pLoading}>
          {pLoading ? 'Looking across your reflections…' : '✨ See patterns across your reflections'}
        </button>
      )}
      {patterns && (
        <div style={s.patternsPanel}>
          <div style={s.patternsHead}>
            <span style={s.patternsTitle}>Threads I notice</span>
            <button style={s.patternsClose} onClick={() => setPatterns(null)}>×</button>
          </div>
          {!patterns.enough && <p style={s.muted}>Not enough yet to see clear threads — keep reflecting and they'll emerge.</p>}
          {patterns.enough && patterns.patterns.map((p, i) => (
            <div key={i} style={s.pattern}>
              <p style={s.patternTitle}>{p.title}</p>
              {p.insight && <p style={s.patternInsight}>{p.insight}</p>}
              {p.gentleStep && <p style={s.patternStep}>{p.gentleStep}</p>}
            </div>
          ))}
          <p style={s.patternsNote}>Gentle observations, not a diagnosis.</p>
        </div>
      )}
      {error && <p style={s.error}>{error}</p>}
      {list === null && <p style={s.muted}>Loading…</p>}
      {list && list.length === 0 && <p style={s.muted}>No reflections yet — tap "New reflection" above to talk through your first one. It will be saved here to come back to.</p>}
      {list && list.length > 1 && (() => {
        const allThemes = [...new Set(list.flatMap(i => Array.isArray(i.themes) ? i.themes : []))].slice(0, 20)
        return allThemes.length > 1 ? (
          <div style={s.filterRow}>
            {themeFilter && <button style={s.filterClear} onClick={() => setThemeFilter(null)}>All ×</button>}
            {allThemes.map((t, i) => (
              <button key={i} style={{ ...s.filterChip, ...(themeFilter === t ? s.filterChipOn : {}) }} onClick={() => setThemeFilter(themeFilter === t ? null : t)}>{t}</button>
            ))}
          </div>
        ) : null
      })()}

      {/* The archive must INVITE continuing, not just record (David, 19 Aug):
          a plain-words header, the most recent reflection featured as "pick up
          where you left off", and every card carrying an explicit Continue
          affordance so tapping is obviously an action, not just a view. */}
      {(() => {
        const filtered = list ? (themeFilter ? list.filter(i => Array.isArray(i.themes) && i.themes.includes(themeFilter)) : list) : []
        if (!filtered.length) return null
        return (
          <>
            <p style={s.listLabel}>Your previous reflections — tap one to continue reflecting on it</p>
            {filtered.map((item, idx) => {
              const featured = idx === 0 && !themeFilter
              return (
                <button key={item.id} style={{ ...s.card, ...(featured ? s.cardFeatured : {}) }} onClick={() => open(item.id)}>
                  {featured && <p style={s.featuredKicker}>◆ Pick up where you left off</p>}
                  <p style={s.cardDate}>{fmtDate(item.generated_at)}</p>
                  {item.summary && <p style={s.cardSummary}>{item.summary}</p>}
                  {Array.isArray(item.themes) && item.themes.length > 0 && (
                    <div style={s.chips}>{item.themes.slice(0, 4).map((t, i) => <span key={i} style={s.chip}>{t}</span>)}</div>
                  )}
                  <p style={s.cardGo}>{featured ? 'Continue this reflection →' : 'Continue →'}</p>
                </button>
              )
            })}
          </>
        )
      })()}
    </div>
  )
}

const s = {
  wrap:       { minHeight:'100vh', background:'linear-gradient(165deg,#161922,#0e1119)', color:'#e8eef4', padding:'1.1rem 1.1rem 3rem', maxWidth:640, margin:'0 auto' },
  top:        { display:'flex', justifyContent:'flex-start' },
  back:       { background:'transparent', border:'none', color:'rgba(240,244,248,.6)', fontSize:'0.9rem', cursor:'pointer', padding:'0.4rem 0' },
  head:       { textAlign:'center', padding:'0.4rem 0 1.1rem' },
  icon:       { fontSize:'2rem' },
  title:      { margin:'0.3rem 0 0.4rem', fontSize:'1.4rem', fontWeight:800, color:'#f0f4f8' },
  sub:        { margin:0, fontSize:'0.88rem', lineHeight:1.55, color:'rgba(240,244,248,.55)' },
  muted:      { color:'rgba(240,244,248,.5)', fontSize:'0.9rem', textAlign:'center', padding:'1.4rem 0' },
  error:      { color:'#ffb4b4', fontSize:'0.82rem', margin:'0.5rem 0' },
  date:       { color:'rgba(240,244,248,.5)', fontSize:'0.78rem', margin:'0.2rem 0 0.8rem' },
  card:       { display:'block', width:'100%', textAlign:'left', background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', borderRadius:'0.9rem', padding:'0.9rem 1rem', marginBottom:'0.6rem', cursor:'pointer' },
  cardFeatured:{ background:'rgba(139,92,246,.10)', border:'1px solid rgba(139,92,246,.45)' },
  featuredKicker:{ margin:'0 0 0.4rem', fontSize:'0.66rem', letterSpacing:'0.13em', textTransform:'uppercase', fontWeight:800, color:'#c9b8ff' },
  listLabel:  { fontSize:'0.7rem', letterSpacing:'0.12em', textTransform:'uppercase', fontWeight:700, color:'rgba(240,244,248,.45)', margin:'0.3rem 0 0.55rem' },
  cardGo:     { margin:'0.55rem 0 0', fontSize:'0.8rem', fontWeight:700, color:'#c9b8ff', textAlign:'right' },
  cardDate:   { margin:'0 0 0.35rem', fontSize:'0.72rem', color:'rgba(240,244,248,.45)' },
  cardSummary:{ margin:'0 0 0.5rem', fontSize:'0.92rem', lineHeight:1.5, color:'#eef2f7' },
  reflection: { margin:0, fontSize:'1.02rem', lineHeight:1.65, color:'#f4f1ff', whiteSpace:'pre-line' },
  voiceBtn:   { marginTop:'0.9rem', background:'rgba(139,92,246,.14)', border:'1px solid rgba(139,92,246,.34)', color:'#e6ddff', fontSize:'0.82rem', fontWeight:600, borderRadius:'1rem', padding:'0.4rem 0.9rem', cursor:'pointer', fontFamily:'inherit' },
  block:      { marginTop:'1.05rem' },
  label:      { fontSize:'0.64rem', letterSpacing:'0.14em', textTransform:'uppercase', color:'rgba(240,244,248,.4)', fontWeight:700, margin:'0 0 0.4rem' },
  summary:    { margin:0, fontSize:'0.9rem', lineHeight:1.6, color:'rgba(240,244,248,.8)' },
  debrief:    { margin:0, fontSize:'0.88rem', lineHeight:1.6, color:'rgba(240,244,248,.65)', fontStyle:'italic' },
  chips:      { display:'flex', flexWrap:'wrap', gap:'0.4rem' },
  chip:       { fontSize:'0.76rem', fontWeight:600, color:'#d9ccff', background:'rgba(139,92,246,.16)', border:'1px solid rgba(139,92,246,.32)', borderRadius:'1rem', padding:'0.26rem 0.68rem' },
  moments:    { margin:0, paddingLeft:'1.1rem', display:'flex', flexDirection:'column', gap:'0.3rem' },
  moment:     { fontSize:'0.88rem', lineHeight:1.5, color:'rgba(240,244,248,.8)' },
  controls:   { display:'flex', gap:'0.6rem', marginTop:'1.3rem', flexWrap:'wrap' },
  primary:    { flex:'1 1 auto', background:'#8b5cf6', border:'none', color:'#fff', fontSize:'0.9rem', fontWeight:700, borderRadius:'0.7rem', padding:'0.75rem 1rem', cursor:'pointer' },
  danger:     { flex:'0 0 auto', background:'transparent', border:'1px solid rgba(239,68,68,.5)', color:'#ffb4b4', fontSize:'0.9rem', fontWeight:600, borderRadius:'0.7rem', padding:'0.75rem 1.1rem', cursor:'pointer' },
  newBtn:       { display:'block', width:'100%', margin:'0 0 0.8rem', background:'linear-gradient(135deg,#5a3a9e,#3a2070)', border:'none', color:'#fff', fontSize:'0.92rem', fontWeight:700, borderRadius:'0.7rem', padding:'0.85rem 1rem', cursor:'pointer' },
  patternsBtn:  { display:'block', width:'100%', margin:'0 0 0.8rem', background:'rgba(139,92,246,.14)', border:'1px solid rgba(139,92,246,.34)', color:'#e6ddff', fontSize:'0.88rem', fontWeight:700, borderRadius:'0.7rem', padding:'0.7rem 1rem', cursor:'pointer' },
  patternsPanel:{ background:'rgba(139,92,246,.08)', border:'1px solid rgba(139,92,246,.28)', borderRadius:'1rem', padding:'1rem', margin:'0 0 1rem' },
  patternsHead: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.5rem' },
  patternsTitle:{ fontSize:'0.7rem', letterSpacing:'0.14em', textTransform:'uppercase', color:'#c9b8ff', fontWeight:800 },
  patternsClose:{ background:'transparent', border:'none', color:'rgba(240,244,248,.5)', fontSize:'1.3rem', lineHeight:1, cursor:'pointer' },
  pattern:      { padding:'0.6rem 0', borderTop:'1px solid rgba(255,255,255,.06)' },
  patternTitle: { margin:'0 0 0.25rem', fontSize:'0.95rem', fontWeight:700, color:'#f0f4f8' },
  patternInsight:{ margin:'0 0 0.35rem', fontSize:'0.88rem', lineHeight:1.55, color:'rgba(240,244,248,.82)' },
  patternStep:  { margin:0, fontSize:'0.85rem', lineHeight:1.5, color:'#d9ccff', fontStyle:'italic' },
  patternsNote: { margin:'0.6rem 0 0', fontSize:'0.7rem', color:'rgba(240,244,248,.4)' },
  filterRow:   { display:'flex', flexWrap:'wrap', gap:'0.4rem', margin:'0 0 0.9rem' },
  filterChip:  { fontSize:'0.76rem', fontWeight:600, color:'rgba(240,244,248,.72)', background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.14)', borderRadius:'1rem', padding:'0.28rem 0.7rem', cursor:'pointer' },
  filterChipOn:{ color:'#fff', background:'rgba(139,92,246,.35)', borderColor:'rgba(139,92,246,.6)' },
  filterClear: { fontSize:'0.76rem', fontWeight:700, color:'#d9ccff', background:'transparent', border:'1px solid rgba(139,92,246,.4)', borderRadius:'1rem', padding:'0.28rem 0.7rem', cursor:'pointer' },
}
